/**
 * customização: Product Card — swipe nativo, quick add, wishlist visual, color preview.
 * Persistência da wishlist fica em wishlist.js (Fase 3); aqui só UI + add to cart.
 */
class ProductCard extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.productId = this.dataset.productId;
    this.scrollEl = this.querySelector('[data-product-card-scroll]');
    this.progressEl = this.querySelector('[data-product-card-progress]');
    this.wishlistBtn = this.querySelector('[data-product-card-wishlist]');
    this.quickAddBtn = this.querySelector('[data-product-card-quick-add]');
    this.dialog = this.querySelector('[data-product-card-dialog]');
    this.cardImages = [
      ...this.querySelectorAll(
        '.product-card__slide .product-card__image, .product-card__media-static .product-card__image'
      ),
    ];
    this.defaultImage = this.cardImages[0] || null;
    this.activeColor = null;

    this.mediaByColor = this.parseJSON(this.querySelector('[data-product-card-media-by-color]')?.textContent, {});
    this.variants = this.parseJSON(this.querySelector('[data-product-card-variants]')?.textContent, []);
    this.colorOptionPosition = Number(this.dataset.colorOptionPosition || 0);
    this.sizeOptionPosition = Number(this.dataset.sizeOptionPosition || 0);

    this.bindSwipeProgress();
    this.bindWishlist();
    this.bindQuickAdd();
    this.bindSizeButtons();
    this.bindColorDots();
    this.syncWishlistFromStore();
  }

  disconnectedCallback() {
    this.scrollEl?.removeEventListener('scroll', this._onScroll);
  }

  parseJSON(raw, fallback) {
    try {
      return JSON.parse(raw || '') || fallback;
    } catch (e) {
      return fallback;
    }
  }

  bindSwipeProgress() {
    // só inicializa swipe/progresso quando o contexto permite (data-image-swipe)
    if (this.dataset.imageSwipe !== 'true') return;
    if (!this.scrollEl || !this.progressEl) return;

    this._onScroll = () => {
      const max = this.scrollEl.scrollWidth - this.scrollEl.clientWidth;
      if (max <= 0) {
        // foto única / sem scroll — ainda assim mostra "1 de 2" = 50%
        this.progressEl.style.width = '50%';
        return;
      }
      // 2 fotos: repouso na 1ª = 50%, 2ª = 100% (nunca 0%)
      const scrollFraction = this.scrollEl.scrollLeft / max;
      const progress = 50 + scrollFraction * 50;
      this.progressEl.style.width = `${Math.max(50, Math.min(100, progress))}%`;
    };

    this.scrollEl.addEventListener('scroll', this._onScroll, { passive: true });
    this._onScroll();
  }

  bindWishlist() {
    if (!this.wishlistBtn) return;

    this.wishlistBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const pressed = this.wishlistBtn.getAttribute('aria-pressed') === 'true';
      const next = !pressed;

      // Fase 3: se Wishlist API existir, persiste; senão só visual
      if (window.DawnWishlist) {
        if (next) {
          window.DawnWishlist.add(this.productId, this.dataset.productHandle);
        } else {
          window.DawnWishlist.remove(this.productId);
        }
      }

      this.setWishlistPressed(next);
    });
  }

  syncWishlistFromStore() {
    if (!this.wishlistBtn || !window.DawnWishlist) return;
    this.setWishlistPressed(window.DawnWishlist.has(this.productId));
  }

  setWishlistPressed(pressed) {
    if (!this.wishlistBtn) return;
    this.wishlistBtn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    this.wishlistBtn.setAttribute(
      'aria-label',
      pressed
        ? this.wishlistBtn.dataset.labelRemove || 'Remover da wishlist'
        : this.wishlistBtn.dataset.labelAdd || 'Adicionar à wishlist'
    );
  }

  bindQuickAdd() {
    if (!this.quickAddBtn || !this.dialog) return;

    this.quickAddBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      }
    });

    // Fechar ao clicar no backdrop (fora do painel)
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
  }

  bindSizeButtons() {
    this.querySelectorAll('[data-product-card-size]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.addSize(button);
      });
    });
  }

  async addSize(button) {
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    const size = button.dataset.size;
    let variantId = button.dataset.variantId;

    if (this.sizeOptionPosition > 0) {
      const resolved = this.resolveVariantId(size);
      if (resolved) variantId = String(resolved);
    }

    if (!variantId || !window.DawnCart?.addVariantAndRefresh) return;

    this.classList.add('is-adding');
    let addedSuccessfully = false;

    try {
      await window.DawnCart.addVariantAndRefresh(variantId, 1);
      addedSuccessfully = true;
      this.dialog?.close();
    } catch (error) {
      console.error(error);
      addedSuccessfully = false;
    } finally {
      this.classList.remove('is-adding');
      document.dispatchEvent(
        new CustomEvent('size-clicked', {
          detail: {
            productId: this.productId,
            size,
            addedSuccessfully,
          },
        })
      );
    }
  }

  resolveVariantId(sizeValue) {
    const colorPos = this.colorOptionPosition;
    const sizePos = this.sizeOptionPosition;
    const color = this.activeColor;

    const matches = this.variants.filter((variant) => {
      if (!variant.available) return false;
      const options = variant.options || [];
      if (sizePos > 0 && options[sizePos - 1] !== sizeValue) return false;
      if (color && colorPos > 0 && options[colorPos - 1] !== color) return false;
      return true;
    });

    if (matches.length) return matches[0].id;

    const anySize = this.variants.find(
      (variant) => variant.available && (!sizePos || variant.options?.[sizePos - 1] === sizeValue)
    );
    return anySize?.id || null;
  }

  variantIdForColor(color) {
    const colorPos = this.colorOptionPosition;
    if (!color || colorPos <= 0) return null;

    const matches = this.variants.filter((variant) => variant.options?.[colorPos - 1] === color);
    const available = matches.find((variant) => variant.available);
    return available?.id || matches[0]?.id || null;
  }

  updateProductLinks(color) {
    const href = this.urlForColor(color);
    if (!href) return;
    this.querySelectorAll('[data-product-card-link]').forEach((link) => {
      link.setAttribute('href', href);
    });
  }

  urlForColor(color) {
    const base = this.dataset.productUrl || '';
    if (!base) return '';
    const variantId = this.variantIdForColor(color);

    try {
      const url = new URL(base, window.location.origin);
      if (variantId) {
        url.searchParams.set('variant', String(variantId));
      } else {
        url.searchParams.delete('variant');
      }
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (e) {
      if (!variantId) return base;
      if (/[?&]variant=/.test(base)) {
        return base.replace(/([?&]variant=)[^&]*/, `$1${variantId}`);
      }
      return `${base}${base.includes('?') ? '&' : '?'}variant=${variantId}`;
    }
  }

  bindColorDots() {
    const dots = this.querySelectorAll('[data-product-card-color]');
    if (!dots.length || !this.cardImages.length) return;

    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    // snapshot das fotos originais (foto 1 + foto 2 do swipe) pra restaurar no mouseleave
    this.defaultImageSources = this.cardImages.map((img) => ({
      src: img.getAttribute('src'),
      srcset: img.getAttribute('srcset'),
    }));
    this.selectedColor = null;

    const sourcesForColor = (color) => {
      const raw = this.mediaByColor[color];
      if (!raw) return [];
      // array (novo) ou string legada (1 src)
      const list = Array.isArray(raw) ? raw.filter(Boolean) : [raw];
      return list;
    };

    const applyImageForColor = (color) => {
      const sources = sourcesForColor(color);
      if (!sources.length) return;

      // atualiza AMBOS os slots do swipe; se a cor só tiver 1 foto, repete no slot 2
      this.cardImages.forEach((imgEl, index) => {
        const src = sources[index] || sources[0];
        if (!src) return;
        imgEl.setAttribute('src', src);
        imgEl.removeAttribute('srcset');
      });

      if (this.scrollEl) this.scrollEl.scrollLeft = 0;
      if (this.progressEl && this.dataset.imageSwipe === 'true') {
        this.progressEl.style.width = '50%';
      }
    };

    const restoreImage = () => {
      if (this.selectedColor && sourcesForColor(this.selectedColor).length) {
        applyImageForColor(this.selectedColor);
        this.activeColor = this.selectedColor;
        return;
      }
      this.activeColor = null;
      this.cardImages.forEach((imgEl, index) => {
        const snapshot = this.defaultImageSources[index];
        if (!snapshot) return;
        if (snapshot.src) imgEl.setAttribute('src', snapshot.src);
        if (snapshot.srcset) {
          imgEl.setAttribute('srcset', snapshot.srcset);
        } else {
          imgEl.removeAttribute('srcset');
        }
      });
    };

    const selectColor = (color) => {
      this.selectedColor = color;
      this.activeColor = color;
      dots.forEach((dot) => {
        const selected = dot.dataset.color === color;
        dot.classList.toggle('is-selected', selected);
        dot.classList.toggle('is-active', selected);
        dot.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      applyImageForColor(color);
      this.updateProductLinks(color);
    };

    dots.forEach((dot) => {
      // clique: seleção persistente na sessão do card
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        selectColor(dot.dataset.color);
      });

      if (!canHover) return;

      // hover: preview temporário (não grava seleção)
      dot.addEventListener('mouseenter', () => {
        this.activeColor = dot.dataset.color;
        dots.forEach((d) => d.classList.toggle('is-active', d === dot || d.classList.contains('is-selected')));
        applyImageForColor(dot.dataset.color);
      });
    });

    if (canHover) {
      this.addEventListener('mouseleave', () => {
        dots.forEach((d) => d.classList.toggle('is-active', d.classList.contains('is-selected')));
        restoreImage();
      });
    }

    const firstDot = dots[0];
    if (firstDot?.dataset.color) {
      selectColor(firstDot.dataset.color);
    }
  }

  /**
   * Constrói HTML do card a partir do JSON de /products/{handle}.js
   * (Recently Viewed / Wishlist). Mantém as mesmas classes do snippet Liquid.
   */
  static buildHTML(product, options = {}) {
    const strings = options.strings || {};
    const settings = options.settings || {};
    const moneyFormat = options.moneyFormat || '${{amount}}';
    const showSwipe = settings.showSecondImage !== false;
    const showWishlist = settings.showWishlist !== false;
    const showQuickAdd = settings.showQuickAdd !== false;
    const showSizeBar = settings.showSizeBar !== false;
    const showColorDots = settings.showColorDots !== false;
    const dotsPosition = settings.colorDotsPosition || 'inline_with_bag';
    const progressHeight = settings.progressBarHeight || 2;
    const aspectRatio = settings.aspectRatio || '0.7';
    const showDiscountBadge = settings.showDiscountBadge !== false;
    const discountPosition = ['bottom_right', 'bottom_left', 'top_left'].includes(settings.discountBadgePosition)
      ? settings.discountBadgePosition
      : 'bottom_right';
    const discountPercent = showDiscountBadge ? ProductCard.discountPercent(product) : 0;

    const images = ProductCard.normalizeImages(product);
    const optionsList = product.options || [];
    const colorName = (options.colorOptionName || 'Cor').toLowerCase();
    const sizeName = (options.sizeOptionName || 'Tamanho').toLowerCase();
    const colorIndex = optionsList.findIndex((name) => String(name).toLowerCase() === colorName);
    const sizeIndex = optionsList.findIndex((name) => {
      const n = String(name).toLowerCase();
      return n === sizeName || /size|tamanho|taille|talla|größe/.test(n);
    });

    const variants = product.variants || [];
    const sizeValues = ProductCard.uniqueOptionValues(variants, sizeIndex);
    const colorValues = ProductCard.uniqueOptionValues(variants, colorIndex);
    const uid = `ProductCard-js-${product.id}-${Math.random().toString(36).slice(2, 8)}`;
    const priceCents = Number(product.price ?? variants[0]?.price ?? 0);
    const compareCents = Number(product.compare_at_price ?? 0);
    const onSale = compareCents > priceCents && priceCents >= 0;
    const formatMoney = (cents) =>
      window.DawnCart?.formatMoney ? window.DawnCart.formatMoney(cents, moneyFormat) : '';
    const salePriceColor = options.salePriceColor || '';
    const salePriceStyle = salePriceColor ? ` style="color:${ProductCard.escapeAttr(salePriceColor)}"` : '';
    // customização: ordem igual ao price.liquid — compare-at riscado primeiro, preço promo depois
    const priceBlock = onSale
      ? `<div class="price price--product-card price--on-sale">
            <div class="price__container">
              <div class="price__sale">
                <span><s class="price-item price-item--regular">${formatMoney(compareCents)}</s></span>
                <span class="price-item price-item--sale price-item--last"${salePriceStyle}>${formatMoney(priceCents)}</span>
              </div>
            </div>
          </div>`
      : `<div class="price price--product-card">
            <div class="price__container">
              <div class="price__regular">
                <span class="price-item price-item--regular">${formatMoney(priceCents)}</span>
              </div>
            </div>
          </div>`;

    const mediaByColor = {};
    colorValues.forEach((value) => {
      mediaByColor[value] = ProductCard.imagesForColor(product, value, colorIndex, images);
    });
    const firstColorImages = colorValues.length ? mediaByColor[colorValues[0]] || [] : [];
    const displayImages = firstColorImages.length ? firstColorImages : images;
    const secondImage = showSwipe && displayImages[1] ? displayImages[1] : null;
    const hasImageSwipe = Boolean(secondImage);
    const cardUrl = ProductCard.urlForColor(product, colorValues[0], colorIndex);

    const sizeButtons = () =>
      sizeValues
        .map((value) => {
          const match =
            variants.find((v) => v.options?.[sizeIndex] === value && v.available) ||
            variants.find((v) => v.options?.[sizeIndex] === value);
          const available = Boolean(match?.available);
          return `<button type="button" class="product-card__size-option${
            available ? '' : ' product-card__size-option--unavailable'
          }" data-size="${ProductCard.escapeAttr(value)}" data-variant-id="${match?.id || ''}" ${
            available ? '' : 'disabled aria-disabled="true"'
          } data-product-card-size>${ProductCard.escapeHTML(value)}</button>`;
        })
        .join('');

    const colorDots =
      showColorDots && colorValues.length
        ? `<ul class="product-card__color-dots" role="list">${colorValues
            .map((value, index) => {
              const variant = variants.find((v) => v.options?.[colorIndex] === value);
              const img = variant?.featured_image;
              const src = typeof img === 'object' ? img?.src : img;
              const style = src
                ? `background-image:url(${src})`
                : `background-color:${ProductCard.escapeAttr(value)}`;
              const selected = index === 0;
              return `<li class="product-card__color-dot-item"><button type="button" class="product-card__color-dot${
                selected ? ' is-selected' : ''
              }" style="${style}" data-color="${ProductCard.escapeAttr(value)}" aria-label="${ProductCard.escapeAttr(
                value
              )}" aria-pressed="${selected ? 'true' : 'false'}" data-product-card-color></button></li>`;
            })
            .join('')}</ul>`
        : '';

    const mediaInner = hasImageSwipe
      ? `<div class="product-card__media-scroll" data-product-card-scroll role="region">
          <div class="product-card__slide" data-slide-index="0" data-default-slide>
            ${
              displayImages[0]
                ? `<img class="product-card__image" src="${ProductCard.escapeAttr(
                    displayImages[0]
                  )}" alt="${ProductCard.escapeAttr(product.title)}" loading="lazy" width="900" height="1200">`
                : ''
            }
          </div>
          <div class="product-card__slide" data-slide-index="1">
            <img class="product-card__image" src="${ProductCard.escapeAttr(
              secondImage
            )}" alt="${ProductCard.escapeAttr(product.title)}" loading="lazy" width="900" height="1200">
          </div>
        </div>`
      : `<div class="product-card__media-static" data-default-slide>
          ${
            displayImages[0]
              ? `<img class="product-card__image" src="${ProductCard.escapeAttr(
                  displayImages[0]
                )}" alt="${ProductCard.escapeAttr(product.title)}" loading="lazy" width="900" height="1200">`
              : ''
          }
        </div>`;

    const quickAddButton = showQuickAdd
      ? `<button type="button" class="product-card__quick-add" aria-label="${ProductCard.escapeAttr(
          strings.quickAdd || 'Adicionar rapidamente'
        )}" data-product-card-quick-add aria-haspopup="dialog" aria-controls="${uid}-dialog">
          <span class="svg-wrapper" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-shopping-bag" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M6 8h12l-1 12H7L6 8z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/></svg>
          </span>
        </button>`
      : '';

    const quickAddDialog = showQuickAdd
      ? `<dialog class="product-card__dialog" id="${uid}-dialog" data-product-card-dialog>
          <div class="product-card__dialog-inner">
            <p class="product-card__dialog-title">${ProductCard.escapeHTML(
              strings.selectSize || 'Select a Size:'
            )}</p>
            <div class="product-card__dialog-sizes">${
              sizeValues.length
                ? sizeButtons()
                : variants
                    .map(
                      (v) =>
                        `<button type="button" class="product-card__size-option${
                          v.available ? '' : ' product-card__size-option--unavailable'
                        }" data-size="${ProductCard.escapeAttr(v.title)}" data-variant-id="${v.id}" ${
                          v.available ? '' : 'disabled aria-disabled="true"'
                        } data-product-card-size>${ProductCard.escapeHTML(v.title)}</button>`
                    )
                    .join('')
            }</div>
          </div>
        </dialog>`
      : '';

    return `
      <product-card
        class="product-card product-card--dots-${ProductCard.escapeAttr(dotsPosition)}"
        style="--card-progress-height: ${Number(progressHeight) || 2}px; --product-card-aspect-ratio: ${ProductCard.escapeAttr(
          String(aspectRatio)
        )};"
        data-product-id="${product.id}"
        data-product-handle="${ProductCard.escapeAttr(product.handle)}"
        data-product-url="${ProductCard.escapeAttr(product.url)}"
        data-money-format="${ProductCard.escapeAttr(moneyFormat)}"
        data-color-option-name="${ProductCard.escapeAttr(options.colorOptionName || 'Cor')}"
        data-size-option-name="${ProductCard.escapeAttr(options.sizeOptionName || 'Tamanho')}"
        data-image-swipe="${hasImageSwipe ? 'true' : 'false'}"
        ${colorIndex >= 0 ? `data-color-option-position="${colorIndex + 1}"` : ''}
        ${sizeIndex >= 0 ? `data-size-option-position="${sizeIndex + 1}"` : ''}
      >
        <div class="product-card__media">
          <a href="${ProductCard.escapeAttr(cardUrl)}" class="product-card__media-link" aria-label="${ProductCard.escapeAttr(
            product.title
          )}" tabindex="-1" data-product-card-link>
            ${mediaInner}
          </a>
          ${
            hasImageSwipe
              ? `<div class="product-card__media-progress" aria-hidden="true"><span class="product-card__media-progress-fill" data-product-card-progress style="width:50%"></span></div>`
              : ''
          }
          ${
            discountPercent > 0
              ? `<span class="product-card__discount-badge product-card__discount-badge--${ProductCard.escapeAttr(
                  discountPosition
                )}" aria-label="${ProductCard.escapeAttr(
                  (strings.discountPercent || '-{{ percent }}%').replace('{{ percent }}', String(discountPercent))
                )}">-${discountPercent}%</span>`
              : ''
          }
          ${
            showWishlist
              ? `<button type="button" class="product-card__wishlist-toggle" data-product-id="${
                  product.id
                }" data-product-handle="${ProductCard.escapeAttr(
                  product.handle
                )}" aria-label="${ProductCard.escapeAttr(
                  strings.addToWishlist || 'Adicionar à wishlist'
                )}" aria-pressed="false" data-product-card-wishlist>
                <span class="product-card__wishlist-icon product-card__wishlist-icon--outline" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 5.24 8.515 3.773a4.433 4.433 0 0 0-6.21 0 4.293 4.293 0 0 0 0 6.128L10 17.495l7.695-7.593a4.293 4.293 0 0 0 0-6.128 4.433 4.433 0 0 0-6.21 0zm.765-2.177c2.113-2.084 5.538-2.084 7.65 0a5.29 5.29 0 0 1 0 7.55l-7.695 7.593a1.03 1.03 0 0 1-1.44 0l-7.696-7.594a5.29 5.29 0 0 1 0-7.549C3.697.98 7.122.98 9.234 3.063l.766.755z"/></svg>
                </span>
                <span class="product-card__wishlist-icon product-card__wishlist-icon--filled" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path d="M10 17.495 2.305 9.902a5.29 5.29 0 0 1 0-7.549c2.112-2.083 5.537-2.083 7.65 0l.045.044.044-.044c2.113-2.083 5.538-2.083 7.65 0a5.29 5.29 0 0 1 0 7.55L10 17.494z"/></svg>
                </span>
              </button>`
              : ''
          }
          ${
            showSizeBar && sizeValues.length
              ? `<div class="product-card__size-bar" data-product-card-size-bar>${sizeButtons()}</div>`
              : ''
          }
          <script type="application/json" data-product-card-media-by-color>${JSON.stringify(mediaByColor)}</script>
          <script type="application/json" data-product-card-variants>${JSON.stringify(
            variants.map((v) => ({
              id: v.id,
              available: v.available,
              options: v.options,
              featured_image:
                typeof v.featured_image === 'object' ? v.featured_image?.src : v.featured_image || null,
            }))
          )}</script>
        </div>
        <div class="product-card__info">
          ${dotsPosition === 'above_name' ? colorDots : ''}
          <a href="${ProductCard.escapeAttr(cardUrl)}" class="product-card__title full-unstyled-link" data-product-card-link>${ProductCard.escapeHTML(
            product.title
          )}</a>
          ${dotsPosition === 'below_name' ? colorDots : ''}
          <div class="product-card__price">${priceBlock}</div>
          ${dotsPosition === 'below_price' ? colorDots : ''}
          <div class="product-card__info-footer">
            ${dotsPosition === 'inline_with_bag' ? colorDots : ''}
            ${quickAddButton}
          </div>
          ${quickAddDialog}
        </div>
      </product-card>
    `;
  }

  static urlForColor(product, colorValue, colorIndex) {
    const base = product?.url || (product?.handle ? `/products/${product.handle}` : '');
    if (!base) return '';
    if (!colorValue || colorIndex < 0) return base;

    const variants = product.variants || [];
    const match =
      variants.find((variant) => variant.options?.[colorIndex] === colorValue && variant.available) ||
      variants.find((variant) => variant.options?.[colorIndex] === colorValue);
    if (!match?.id) return base;

    if (/[?&]variant=/.test(base)) {
      return base.replace(/([?&]variant=)[^&]*/, `$1${match.id}`);
    }
    return `${base}${base.includes('?') ? '&' : '?'}variant=${match.id}`;
  }

  static normalizeImages(product) {
    const list = [];
    const push = (value) => {
      if (!value) return;
      const src = typeof value === 'string' ? value : value.src;
      if (src && !list.includes(src)) list.push(src.startsWith('//') ? `https:${src}` : src);
    };

    push(product.featured_image);
    (product.images || []).forEach(push);
    (product.media || []).forEach((media) => {
      if (media?.media_type && media.media_type !== 'image') return;
      push(media.src || media.preview_image);
    });
    return list;
  }

  /** Mesma fórmula do Liquid: % off a partir de product.compare_at_price vs product.price */
  static discountPercent(product) {
    const price = Number(product?.price);
    const compare = Number(product?.compare_at_price);
    if (!price || !compare || compare <= price) return 0;
    const percent = Math.round(((compare - price) * 100) / compare);
    return percent > 0 ? percent : 0;
  }

  /**
   * Até 2 imagens da cor (Alt text = nome da cor), mesma convenção da galeria PDP.
   * Fallback: featured da variante + próxima imagem do produto; se só 1, o slot 2
   * repete a 1ª no applyImageForColor.
   */
  static imagesForColor(product, colorValue, colorIndex, imagesFallback = []) {
    const colorDown = String(colorValue || '')
      .toLowerCase()
      .trim();
    const matched = [];
    const abs = (src) => (src?.startsWith('//') ? `https:${src}` : src);

    (product.media || []).forEach((media) => {
      if (matched.length >= 2) return;
      if (media?.media_type && media.media_type !== 'image') return;
      const alt = String(media.alt || '')
        .toLowerCase()
        .trim();
      if (alt !== colorDown) return;
      const src = abs(media.src || media.preview_image?.src);
      if (src && !matched.includes(src)) matched.push(src);
    });

    if (matched.length) return matched;

    const variants = product.variants || [];
    const variant =
      colorIndex >= 0
        ? variants.find((v) => v.options?.[colorIndex] === colorValue && v.featured_image)
        : null;
    const featured = variant?.featured_image;
    const src0 = abs(typeof featured === 'object' ? featured?.src : featured) || imagesFallback[0];
    if (!src0) return [];

    const all = imagesFallback.length ? imagesFallback : ProductCard.normalizeImages(product);
    const idx = all.findIndex((src) => src === src0 || src.split('?')[0] === src0.split('?')[0]);
    const src1 = (idx >= 0 ? all[idx + 1] : null) || all.find((src) => src !== src0) || null;
    return src1 ? [src0, src1] : [src0];
  }

  static uniqueOptionValues(variants, index) {
    if (index < 0) return [];
    const values = [];
    variants.forEach((variant) => {
      const value = variant.options?.[index];
      if (value && !values.includes(value)) values.push(value);
    });
    return values;
  }

  static escapeHTML(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  static escapeAttr(value) {
    return ProductCard.escapeHTML(value).replace(/'/g, '&#39;');
  }

  static skeletonHTML() {
    return '<div class="product-card product-card--skeleton" aria-hidden="true"></div>';
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

window.ProductCard = ProductCard;
